// Convert a Vimeo / YouTube / Threads / Instagram URL into an embeddable iframe src.
// Returns null if no recognizable embed could be made.
export function toEmbedSrc(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, "");

        // YouTube
        if (host === "youtu.be") {
            return `https://www.youtube.com/embed/${u.pathname.slice(1)}?rel=0&modestbranding=1`;
        }
        if (host.endsWith("youtube.com")) {
            if (u.pathname.startsWith("/embed/")) return url;
            const v = u.searchParams.get("v");
            if (v) return `https://www.youtube.com/embed/${v}?rel=0&modestbranding=1`;
            // /shorts/{id}
            const m = u.pathname.match(/^\/shorts\/([^/]+)/);
            if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1`;
        }

        // Vimeo
        if (host === "player.vimeo.com") return url;
        if (host.endsWith("vimeo.com")) {
            const id = u.pathname.split("/").filter(Boolean)[0];
            if (id) return `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0`;
        }

        // Threads — append /embed if not already there
        if (host.endsWith("threads.com") || host.endsWith("threads.net")) {
            const clean = u.pathname.replace(/\/embed\/?$/, "");
            return `https://www.threads.com${clean}/embed`;
        }

        // Instagram
        if (host.endsWith("instagram.com")) {
            const m = u.pathname.match(/^\/(?:p|reel|tv)\/([^/]+)/);
            if (m) return `https://www.instagram.com/p/${m[1]}/embed/`;
        }

        return url; // unknown — try as-is
    } catch {
        return null;
    }
}

// Best guess source label from a URL
export function detectSource(url) {
    if (!url) return "upload";
    try {
        const h = new URL(url).hostname.replace(/^www\./, "");
        if (h.endsWith("threads.com") || h.endsWith("threads.net")) return "threads";
        if (h.endsWith("instagram.com")) return "instagram";
        if (h.endsWith("vimeo.com") || h === "player.vimeo.com") return "vimeo";
        if (h.endsWith("youtube.com") || h === "youtu.be") return "youtube";
    } catch {
        /* ignore */
    }
    return "upload";
}

// Direct-video file extensions we can render with native <video>.
const VIDEO_EXT = /\.(mp4|webm|m4v|mov|ogv)(\?|#|$)/i;
export function isDirectVideo(url) {
    if (!url) return false;
    return VIDEO_EXT.test(url);
}

/**
 * Build an embed src configured for the Featured Reel modal.
 * - Vimeo / YouTube: forces autoplay (and mute on YouTube to satisfy browser policies)
 * - Threads / Instagram / unknown: best-effort iframe (no programmatic autoplay)
 * Returns null if no embed could be built.
 */
export function toReelEmbedSrc(url) {
    const base = toEmbedSrc(url);
    if (!base) return null;
    try {
        const u = new URL(base);
        const host = u.hostname.replace(/^www\./, "");
        if (host.endsWith("vimeo.com")) {
            u.searchParams.set("autoplay", "1");
            u.searchParams.set("muted", "0");
            return u.toString();
        }
        if (host.endsWith("youtube.com") || host === "youtu.be") {
            u.searchParams.set("autoplay", "1");
            // YouTube requires mute for autoplay on most browsers
            u.searchParams.set("mute", "1");
            u.searchParams.set("playsinline", "1");
            return u.toString();
        }
        return base;
    } catch {
        return base;
    }
}

// True for sources we can attempt to autoplay programmatically via URL params.
export function isAutoplayableEmbed(source) {
    return source === "vimeo" || source === "youtube";
}
